'use client';

import { Button, DialogContent, Dialogß } from '@otter-ai/den-components';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

export type RevokeAccessAction = 'remove' | 'deactivate';

export type RevokeAccessResult = {
  confirmed: true;
  action: RevokeAccessAction;
} | null;

type RevokeAccessDialogProps = {
  done: (result: RevokeAccessResult) => void;
  memberName: string;
  memberEmail: string;
  workspaceName: string;
  /** Whether this is for multiple members (bulk action) */
  isBulk?: boolean;
  memberCount?: number;
  /** List of member emails for bulk actions */
  memberEmails?: string[];
  /** Whether to show the "Remove from Workspace" option (hidden for claimed domain workspaces) */
  showRemoveOption?: boolean;
  /** Default action to pre-select ('remove' for normal, 'deactivate' for sales-assisted) */
  defaultAction?: RevokeAccessAction;
  isSalesAssisted?: boolean;
};

export function RevokeAccessDialog({
  done,
  memberName,
  memberEmail,
  workspaceName,
  isBulk = false,
  memberCount = 1,
  memberEmails = [],
  showRemoveOption = true,
  defaultAction = 'remove',
  isSalesAssisted = false,
}: RevokeAccessDialogProps) {
  const t = useTranslations('manage-team:team-profile');
  const [selectedAction, setSelectedAction] =
    useState<RevokeAccessAction>(defaultAction);

  const plural = isBulk && memberCount > 1;
  const displayEmails = isBulk
    ? memberEmails.join(', ')
    : `${memberName} (${memberEmail})`;

  return (
    <Dialogß
      isOpen
      onOpenChange={(open) => {
        if (!open) done(null);
      }}
      title={t('dialogs.revokeAccess.title')}
      className="max-w-[560px]"
      data-testid="revoke-access-dialog"
      Footer={
        <>
          <Button
            variant="outlineNeutral"
            version="legacy"
            onClick={() => done(null)}
            data-testid="revoke-access-cancel-button"
          >
            {t('dialogs.common.cancel')}
          </Button>
          <Button
            variant="negative"
            onClick={() => done({ confirmed: true, action: selectedAction })}
            data-testid="revoke-access-confirm-button"
          >
            {t('dialogs.revokeAccess.confirm')}
          </Button>
        </>
      }
    >
      <DialogContent>
        <div className="space-y-4">
          {!isSalesAssisted && (
            <p className="text-subtle text-sm font-medium">
              {t('dialogs.revokeAccess.subtitle')}
            </p>
          )}
          <div className="space-y-2">
            <p>
              {t('dialogs.revokeAccess.aboutToRevoke', {
                workspaceName,
                count: plural ? 2 : 1,
              })}
            </p>
            <p className="font-semibold">{displayEmails}</p>
          </div>

          <p className="text-subtle">
            {isSalesAssisted
              ? t('dialogs.revokeAccess.sharedConversationsAccessible')
              : t('dialogs.revokeAccess.noBilling', { count: plural ? 2 : 1 })}
          </p>

          <div className="space-y-3 pt-2">
            {showRemoveOption && (
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="revoke-action"
                  value="remove"
                  checked={selectedAction === 'remove'}
                  onChange={() => setSelectedAction('remove')}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium">
                    {t('dialogs.revokeAccess.removeOption.title', {
                      count: plural ? 2 : 1,
                    })}
                  </p>
                  <p className="text-sm text-subtle">
                    {t('dialogs.revokeAccess.removeOption.description', {
                      count: plural ? 2 : 1,
                    })}
                  </p>
                </div>
              </label>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="revoke-action"
                value="deactivate"
                checked={selectedAction === 'deactivate'}
                onChange={() => setSelectedAction('deactivate')}
                className="mt-1"
              />
              <div>
                <p className="font-medium">
                  {t('dialogs.revokeAccess.deactivateOption.title', {
                    count: plural ? 2 : 1,
                  })}
                </p>
                <p className="text-sm text-subtle">
                  {t('dialogs.revokeAccess.deactivateOption.description', {
                    count: plural ? 2 : 1,
                  })}
                </p>
              </div>
            </label>
          </div>

          <p className="pt-2 font-medium">
            {isBulk
              ? t.rich('dialogs.revokeAccess.confirmBulk', {
                  count: memberCount,
                  strong: (chunks) => <strong>{chunks}</strong>,
                })
              : t.rich('dialogs.revokeAccess.confirmSingle', {
                  name: memberName,
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
          </p>
        </div>
      </DialogContent>
    </Dialogß>
  );
}
